"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const lodash_1 = require("lodash");
const ts_validators_1 = require("ts-validators");
const qs_1 = require("qs");
const valueIsNullOrUndef_1 = __importDefault(require("../typeAssertions/valueIsNullOrUndef"));
const valueIsRecord_1 = __importDefault(require("../typeAssertions/valueIsRecord"));
const getNumberFromUnknown_1 = __importDefault(require("./getNumberFromUnknown"));
const evaluateInterpolation = ({ inputValues, string }) => {
    let interpolatedString = string;
    const interpolationMatches = string.match(/\$\{[^]+?\}/g);
    if (interpolationMatches) {
        interpolationMatches.forEach((interpolationString) => {
            const inputName = interpolationString
                .replace(/^\$\{/, '').replace(/\}$/, '');
            if (inputValues[inputName]) {
                interpolatedString = interpolatedString
                    .replace(interpolationString, `${inputValues[inputName]}`);
            }
        });
    }
    return interpolatedString;
};
const evaluateRequestFieldValue = ({ requestField, inputFields, inputValues, }) => {
    switch (requestField.type) {
        case 'field': {
            const fieldKey = requestField.value;
            const inputValue = inputValues[fieldKey];
            const inputField = inputFields[fieldKey];
            if ((0, valueIsNullOrUndef_1.default)(inputValue)) {
                if (inputField === null || inputField === void 0 ? void 0 : inputField.defaultValue) {
                    return inputField.defaultValue;
                }
                return undefined;
            }
            if ((inputField === null || inputField === void 0 ? void 0 : inputField.type) === 'number') {
                try {
                    return (0, getNumberFromUnknown_1.default)(inputValue);
                }
                catch (_a) {
                    return inputValue;
                }
            }
            if ((inputField === null || inputField === void 0 ? void 0 : inputField.type) === 'string' && typeof inputValue !== 'string') {
                return JSON.stringify(inputValue);
            }
            return inputValue;
        }
        case 'value':
            // Applies interpolation
            if (typeof requestField.value === 'string') {
                return evaluateInterpolation({
                    inputValues,
                    string: requestField.value,
                });
            }
            return requestField.value;
        default:
            throw new Error('Request field type is not valid');
    }
};
const buildEvaluatedValues = ({ definitionObject = {}, inputFields, inputValues, }) => {
    const evaluatedValues = {};
    Object.entries(definitionObject).forEach(([, requestField]) => {
        var _a;
        const fieldValue = evaluateRequestFieldValue({
            requestField, inputFields, inputValues,
        });
        if ((0, valueIsNullOrUndef_1.default)(fieldValue)) {
            return;
        }
        if (requestField.path === ''
            && (0, valueIsRecord_1.default)(fieldValue)) {
            Object.entries(fieldValue).forEach(([fieldPropName, fieldPropValue]) => {
                evaluatedValues[fieldPropName] = fieldPropValue;
            });
            return;
        }
        const pathArray = ((_a = requestField.path) === null || _a === void 0 ? void 0 : _a.split('.')) || [];
        (0, lodash_1.set)(evaluatedValues, pathArray, fieldValue);
    });
    return evaluatedValues;
};
const evaluateRequest = ({ initialURL, initialParams = {}, initialHeaders = {}, initialData = {}, requestDefinition, inputFields, inputValues, }) => {
    let url = initialURL;
    let params = initialParams;
    let headers = initialHeaders;
    let data = initialData;
    const { concatParams, requestBody: definitionRequestBody, urlParams: definitionUrlParams, httpHeaders: definitionHeaders, } = requestDefinition;
    if (concatParams) {
        url = concatParams.reduce((newUrl, requestField) => {
            const valueToConcat = evaluateRequestFieldValue({
                requestField,
                inputFields,
                inputValues,
            });
            return `${newUrl}/${valueToConcat}`;
        }, url);
    }
    if (definitionUrlParams) {
        params = buildEvaluatedValues({
            definitionObject: definitionUrlParams,
            inputFields,
            inputValues,
        });
    }
    if (definitionHeaders) {
        const rawHeaders = buildEvaluatedValues({
            definitionObject: definitionHeaders,
            inputFields,
            inputValues,
        });
        headers = Object.entries(rawHeaders)
            .reduce((accum, [propName, propValue]) => {
            if ((0, ts_validators_1.valueIsString)(propValue)
                || (0, ts_validators_1.valueIsNumber)(propValue)
                || (0, ts_validators_1.valueIsBoolean)(propValue)) {
                return Object.assign(Object.assign({}, accum), { [propName]: propValue });
            }
            return accum;
        }, {});
    }
    if (definitionRequestBody) {
        data = buildEvaluatedValues({
            definitionObject: definitionRequestBody,
            inputFields,
            inputValues,
        });
    }
    return {
        url,
        params,
        headers,
        data,
    };
};
const mergeRequestDefinitions = (...definitions) => {
    const requestDefinition = {};
    definitions.filter((definition) => definition)
        .forEach((definition) => {
        if (definition.concatParams) {
            requestDefinition.concatParams = [
                ...(requestDefinition.concatParams || []),
                ...definition.concatParams,
            ];
        }
        if (definition.httpHeaders) {
            requestDefinition.httpHeaders = Object.assign(Object.assign({}, (requestDefinition.httpHeaders || {})), definition.httpHeaders);
        }
        if (definition.requestBody) {
            requestDefinition.requestBody = Object.assign(Object.assign({}, (requestDefinition.requestBody || {})), definition.requestBody);
        }
        if (definition.urlParams) {
            requestDefinition.urlParams = Object.assign(Object.assign({}, (requestDefinition.urlParams || {})), definition.urlParams);
        }
        if (definition.shouldOmitEmptyParameters !== undefined) {
            requestDefinition.shouldOmitEmptyParameters = !!definition.shouldOmitEmptyParameters;
        }
        if (definition.shouldQueryStringRequestBody !== undefined) {
            requestDefinition.shouldQueryStringRequestBody = !!definition
                .shouldQueryStringRequestBody;
        }
    });
    return requestDefinition;
};
const buildHttpsRequest = ({ baseDefinition, baseInputValues, baseInputFields, endPoint, inputFields, inputValues, requestType, requestDefinition = {}, }) => {
    const allInputFields = Object.assign(Object.assign({}, baseInputFields), inputFields);
    const allInputValues = Object.assign(Object.assign({}, baseInputValues), inputValues);
    const baseURL = (baseDefinition.baseURL ? evaluateInterpolation({
        inputValues: allInputValues,
        string: baseDefinition.baseURL,
    }) : '').replace(/\$\{[^]+?\}/g, '');
    const totalDefinition = mergeRequestDefinitions(baseDefinition.requestDefinition || {}, requestDefinition);
    const interpolatedEndPoint = endPoint && evaluateInterpolation({
        inputValues: allInputValues,
        string: endPoint,
    });
    const url = [baseURL, interpolatedEndPoint].filter((link) => !!link).join('/');
    const requestObject = evaluateRequest({
        initialURL: url,
        requestDefinition: totalDefinition,
        inputFields: allInputFields,
        inputValues: allInputValues,
    });
    if (totalDefinition.shouldQueryStringRequestBody) {
        requestObject.data = (0, qs_1.stringify)(requestObject.data);
    }
    const requestConfig = {
        url: requestObject.url,
        method: requestType,
    };
    const originalParams = Object.entries(requestObject.params || {});
    if (originalParams.length) {
        requestConfig.params = originalParams.reduce((newParams, [key, value]) => (Object.assign(Object.assign({}, newParams), { [decodeURI(key)]: value })), {});
    }
    if (requestObject.headers && Object.keys(requestObject.headers).length > 0) {
        requestConfig.headers = requestObject.headers;
    }
    if (requestObject.data) {
        if (typeof requestObject.data === 'string') {
            requestConfig.data = requestObject.data;
        }
        else if (Object.keys(requestObject.data).length > 0) {
            requestConfig.data = requestObject.data;
        }
    }
    return requestConfig;
};
exports.default = buildHttpsRequest;
